/**
 * EditMealServingsButton - Button component for editing meal servings and participants
 *
 * This component displays serving information (participants, leftovers, cook time, dishes)
 * and triggers the EditMealServings modal when clicked (if editable).
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isScalable, getLoggedServingsOfRecipe, getParticipantsForSpecificMeal as getParticipantsForSpecificMealFromPlan } from '@utils/Meals';
import { toHuman } from '@utils/Duration';

const EditMealServingsButton = ({
    primary,
    sortedMeals,
    profile,
    plan,
    recipes,
    onEditMealServings
}) => {
    // Calculate serving data for the button
    const servingData = useMemo(() => {
        // Check if the meal is scalable
        const canEdit = isScalable(primary);

        // Check if this is a leftover meal
        const isLeftoverMeal = sortedMeals.some(meal => meal.meal_type === 'leftover');

        // Calculate leftovers - need to find leftovers for ALL dishes in this slot, not just primary
        const dishIds = sortedMeals.map(meal => meal.id);
        const leftovers = plan.items ? plan.items.filter(
            (m) => m.meal_type == 'leftover' && !m.deleted && dishIds.includes(m.parent)
        ) : [];

        // Calculate participants for this meal
        const participants = getParticipantsForSpecificMealFromPlan(primary.primary, profile);
        const participantCount = participants.length;

        // Calculate leftover servings
        const isMultiDish = sortedMeals.length > 1;
        let totalLeftoverServings = 0;
        let leftoverDishBreakdown = [];

        if (isMultiDish) {
            // For multi-dish: calculate per-dish breakdown
            sortedMeals.forEach((meal, dishIndex) => {
                const dishId = meal.id;
                const dishName = recipes[meal.recipe_uuid]?.title || recipes[meal.food_uuid]?.name || `Dish ${dishIndex + 1}`;

                // Find all leftovers for this specific dish
                const dishLeftovers = leftovers.filter(l => l.parent === dishId);

                // Group leftovers by slot (offset + meal)
                const leftoversBySlot = {};
                dishLeftovers.forEach(leftover => {
                    const key = `${leftover.offset}_${leftover.meal}`;
                    if (!leftoversBySlot[key]) {
                        leftoversBySlot[key] = {
                            leftover: leftover,
                            participants: getParticipantsForSpecificMealFromPlan(leftover, profile)
                        };
                    }
                });

                // Calculate total servings for this dish across all leftover slots
                let dishTotalServings = 0;
                Object.values(leftoversBySlot).forEach(({ leftover, participants }) => {
                    participants.forEach(participant => {
                        if (participant.uuid === 'USER') {
                            const dishRecipe = recipes[leftover.recipe_uuid] || recipes[leftover.food_uuid];
                            dishTotalServings += getLoggedServingsOfRecipe(leftover, dishRecipe) || participant.portion;
                        } else {
                            dishTotalServings += participant.portion;
                        }
                    });
                });

                if (dishTotalServings > 0) {
                    leftoverDishBreakdown.push({
                        dishNumber: dishIndex + 1,
                        dishName: dishName,
                        servings: dishTotalServings
                    });
                    totalLeftoverServings += dishTotalServings;
                }
            });
        } else {
            // For single dish: calculate total leftover servings
            const leftoversBySlot = {};
            leftovers.forEach(leftover => {
                const key = `${leftover.offset}_${leftover.meal}`;
                if (!leftoversBySlot[key]) {
                    leftoversBySlot[key] = {
                        leftover: leftover,
                        participants: getParticipantsForSpecificMealFromPlan(leftover, profile)
                    };
                }
            });

            Object.values(leftoversBySlot).forEach(({ leftover, participants }) => {
                participants.forEach(participant => {
                    if (participant.uuid === 'USER') {
                        const dishRecipe = recipes[leftover.recipe_uuid] || recipes[leftover.food_uuid];
                        totalLeftoverServings += getLoggedServingsOfRecipe(leftover, dishRecipe) || participant.portion;
                    } else {
                        totalLeftoverServings += participant.portion;
                    }
                });
            });
        }

        const extraServings = sortedMeals.find(m => m.extra_servings)?.extra_servings || 0;
        totalLeftoverServings += extraServings;

        // Calculate maximum total_time from all meals
        let totalTime = 0;
        sortedMeals.forEach((meal) => {
            const recipe = recipes[meal.recipe_uuid];
            if (recipe && recipe.total_time && recipe.total_time > totalTime) {
                totalTime = recipe.total_time;
            }
        });

        return {
            canEdit,
            isLeftoverMeal,
            participantCount,
            totalLeftoverServings,
            leftoverDishBreakdown,
            totalTime,
            isMultiDish
        };
    }, [primary, sortedMeals, profile, plan.items, recipes]);

    const handleClick = () => {
        if (!servingData.canEdit) {
            return;
        }
        onEditMealServings();
    };

    const { canEdit, isLeftoverMeal, participantCount, totalLeftoverServings, leftoverDishBreakdown, totalTime, isMultiDish } = servingData;

    // Helper for conditional CSS classes
    const getSectionClassName = (baseClass) =>
        `${baseClass}${!canEdit ? ' disabled' : ''}`;

    return (
        <div className="edit-meal-servings" onClick={handleClick}>
            <section className="serves">
                <div>
                    <div>{isLeftoverMeal ? 'Leftover Meal' : 'This Meal Serves'}</div>
                    <div className="servings">{participantCount} {participantCount === 1 ? 'person' : 'people'}</div>
                </div>

                {totalLeftoverServings > 0 ? <div className="plus">+</div> : null}

                {totalLeftoverServings > 0 ? (
                    <div>
                        <div>Leftover Servings</div>
                        {isMultiDish && leftoverDishBreakdown.length > 0 ? (
                            <div className="servings leftover-breakdown">
                                {leftoverDishBreakdown.map((dish, index) => (
                                    <div key={index}>Dish {dish.dishNumber}: {dish.servings} {dish.servings === 1 ? 'serving' : 'servings'}</div>
                                ))}
                            </div>
                        ) : (
                            <div className="servings">{totalLeftoverServings} {totalLeftoverServings === 1 ? 'serving' : 'servings'}</div>
                        )}
                    </div>
                ) : null}

                <div className="edit">EDIT</div>
            </section>
            <div className="timings">
                <span className="stats-text">
                    <p className="dist-count">{sortedMeals.length} DISH{sortedMeals.length !== 1 ? 'ES' : ''}</p>
                    {totalTime > 0 && (
                        <p>
                            {toHuman(totalTime, true)}
                            <br />
                            PREP/COOK
                        </p>
                    )}
                </span>
            </div>
        </div>
    );
};

EditMealServingsButton.propTypes = {
    primary: PropTypes.object.isRequired,
    sortedMeals: PropTypes.array.isRequired,
    profile: PropTypes.object.isRequired,
    plan: PropTypes.object.isRequired,
    recipes: PropTypes.object.isRequired,
    onEditMealServings: PropTypes.func.isRequired,
};

export default EditMealServingsButton;
